import { useState, useEffect, useMemo, Fragment } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { getErrorMessage } from '../utils/errorHandler';
import { toast } from 'sonner';
import { Plus, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import LogoLoader from '@/components/LogoLoader';

const initialConsultantForm = {
  name: '',
  email: '',
  country: '',
  pay_currency: 'USD',
  default_pay_rate: '',
  default_bill_rate: '',
  hr_consultant_id: '',
  hr_commission_amount: '',
  supervising_consultant_id: '',
  mentoring_rate: '',
  skills: '',
  consultant_type: 'Individual (1099)',
  vendor_id: ''
};

const initialVendorForm = {
  name: '',
  legal_name: '',
  tax_id: '',
  country: '',
  currency: 'USD',
  payment_terms: 'Net 30',
  bank_details: '',
  email: '',
  phone: '',
  address: '',
  notes: ''
};

const Consultants = () => {
  const { token, user } = useAuth();
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingConsultant, setEditingConsultant] = useState(null);
  const [formData, setFormData] = useState(() => ({ ...initialConsultantForm }));
  const [hrConsultants, setHrConsultants] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorFilter, setVendorFilter] = useState('all');
  const [groupByVendor, setGroupByVendor] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorForm, setVendorForm] = useState(() => ({ ...initialVendorForm }));

  useEffect(() => {
    loadData();
  }, []);

  const normalizeArrayResponse = (payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }
    if (Array.isArray(payload?.data)) {
      return payload.data;
    }
    return [];
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [consultantsData, hrData, vendorsData] = await Promise.all([
        api.get('/consultants', token),
        api.get('/hr-consultants', token),
        api.get('/vendors', token)
      ]);
      setConsultants(normalizeArrayResponse(consultantsData));
      setHrConsultants(normalizeArrayResponse(hrData));
      setVendors(normalizeArrayResponse(vendorsData));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isVendorConsultant = formData.consultant_type?.includes('Vendor');
    if (isVendorConsultant && !formData.vendor_id) {
      toast.error('Please select a Vendor for Vendor-supplied (C2C) consultants');
      return;
    }

    try {
      const vendorId = isVendorConsultant ? (formData.vendor_id ? String(formData.vendor_id) : null) : null;
      const payload = {
        ...formData,
        consultant_type: formData.consultant_type || 'Individual (1099)',
        vendor_id: vendorId,
        default_pay_rate: parseFloat(formData.default_pay_rate),
        default_bill_rate: formData.default_bill_rate ? parseFloat(formData.default_bill_rate) : null,
        hr_consultant_id: formData.hr_consultant_id || null,
        hr_commission_amount: formData.hr_commission_amount ? parseFloat(formData.hr_commission_amount) : 0,
        supervising_consultant_id: formData.supervising_consultant_id || null,
        mentoring_rate: formData.mentoring_rate ? parseFloat(formData.mentoring_rate) : 0,
        skills: formData.skills.split(',').map((s) => s.trim()).filter(Boolean)
      };

      if (payload.consultant_type.includes('Individual')) {
        payload.vendor_id = null;
      }

      if (editingConsultant) {
        await api.patch(`/consultants/${editingConsultant.id}`, payload, token);
        toast.success('Consultant updated successfully');
      } else {
        await api.post('/consultants', payload, token);
        toast.success('Consultant created successfully');
      }

      setShowDialog(false);
      setEditingConsultant(null);
      loadData();
      setFormData(() => ({ ...initialConsultantForm }));
    } catch (error) {
      toast.error(editingConsultant ? 'Failed to update consultant' : 'Failed to create consultant');
    }
  };

  const openEditDialog = (consultant) => {
    setEditingConsultant(consultant);
    const skills = consultant.skills;
    const normalizedSkills = Array.isArray(skills)
      ? skills
      : typeof skills === 'string'
        ? skills.split(',').map((skill) => skill.trim()).filter(Boolean)
        : [];

    setFormData({
      name: consultant.name,
      email: consultant.email,
      country: consultant.country || '',
      pay_currency: consultant.pay_currency,
      default_pay_rate: consultant.default_pay_rate?.toString() || '',
      default_bill_rate: consultant.default_bill_rate ? consultant.default_bill_rate.toString() : '',
      hr_consultant_id: consultant.hr_consultant_id || '',
      hr_commission_amount: consultant.hr_commission_amount ? consultant.hr_commission_amount.toString() : '',
      supervising_consultant_id: consultant.supervising_consultant_id || '',
      mentoring_rate: consultant.mentoring_rate ? consultant.mentoring_rate.toString() : '',
      skills: normalizedSkills.join(', '),
      consultant_type: consultant.consultant_type || 'Individual (1099)',
      vendor_id: consultant.vendor_id ? String(consultant.vendor_id) : ''
    });
    setShowDialog(true);
  };

  const handleDialogClose = (open) => {
    setShowDialog(open);
    if (!open) {
      setEditingConsultant(null);
      setFormData(() => ({ ...initialConsultantForm }));
    }
  };

  const toggleConsultantStatus = async (consultantId, currentStatus) => {
    try {
      await api.patch(`/consultants/${consultantId}`, { is_active: !currentStatus }, token);
      toast.success(`Consultant ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      loadData();
    } catch (error) {
      console.error('Toggle consultant status error:', error);
      toast.error(getErrorMessage(error, 'Failed to update consultant status'));
    }
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    try {
      const newVendor = await api.post('/vendors', vendorForm, token);
      toast.success('Vendor created');
      setVendors((prev) => [newVendor, ...prev]);
      setVendorForm(() => ({ ...initialVendorForm }));
      setShowVendorModal(false);
      if (formData.consultant_type?.includes('Vendor')) {
        setFormData((prev) => ({ ...prev, vendor_id: newVendor.id ? String(newVendor.id) : prev.vendor_id }));
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create vendor'));
    }
  };

  const handleVendorModalChange = (open) => {
    setShowVendorModal(open);
    if (!open) {
      setVendorForm(() => ({ ...initialVendorForm }));
    }
  };

  const vendorMap = useMemo(() => {
    const map = new Map();
    vendors.forEach((vendor) => {
      map.set(String(vendor.id), vendor);
    });
    return map;
  }, [vendors]);

  const filteredConsultants = useMemo(() => {
    return consultants.filter((consultant) => {
      if (vendorFilter === 'all') return true;
      const consultantVendorId = consultant.vendor_id ? String(consultant.vendor_id) : '';
      return consultantVendorId === vendorFilter;
    });
  }, [consultants, vendorFilter]);

  const groupedConsultants = useMemo(() => {
    if (!groupByVendor) {
      return [];
    }

    const groups = filteredConsultants.reduce((acc, consultant) => {
      const key = consultant.vendor_id ? String(consultant.vendor_id) : 'individual';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(consultant);
      return acc;
    }, {});

    const entries = Object.entries(groups).sort(([keyA], [keyB]) => {
      if (keyA === 'individual') return -1;
      if (keyB === 'individual') return 1;
      const nameA = vendorMap.get(keyA)?.name || `Vendor ${keyA}`;
      const nameB = vendorMap.get(keyB)?.name || `Vendor ${keyB}`;
      return nameA.localeCompare(nameB);
    });

    return entries;
  }, [filteredConsultants, groupByVendor, vendorMap]);

  const renderConsultantRow = (consultant) => {
    const consultantType = consultant.consultant_type || 'Individual (1099)';
    const isVendor = consultantType.includes('Vendor');
    const vendorName =
      isVendor && consultant.vendor_id
        ? vendorMap.get(String(consultant.vendor_id))?.name || `Vendor ${consultant.vendor_id}`
        : null;
    const skillsList = Array.isArray(consultant.skills)
      ? consultant.skills
      : typeof consultant.skills === 'string'
        ? consultant.skills.split(',').map((skill) => skill.trim()).filter(Boolean)
        : [];

    return (
      <TableRow key={consultant.id} className={!consultant.is_active ? 'opacity-60 bg-gray-50' : ''}>
        <TableCell className="font-medium">{consultant.name}</TableCell>
        <TableCell>
          <div>
            <span
              className={`px-2 py-0.5 text-xs rounded ${
                isVendor ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'
              }`}
            >
              {isVendor ? 'Vendor (C2C)' : 'Individual (1099)'}
            </span>
            {isVendor && vendorName && <div className="text-xs text-gray-500 mt-1">{vendorName}</div>}
          </div>
        </TableCell>
        <TableCell>{consultant.email}</TableCell>
        <TableCell>{consultant.country || '-'}</TableCell>
        <TableCell>
          {consultant.default_pay_rate !== undefined && consultant.default_pay_rate !== null
            ? `$${consultant.default_pay_rate}/hr`
            : '-'}
        </TableCell>
        <TableCell>
          {consultant.default_bill_rate !== undefined && consultant.default_bill_rate !== null
            ? `$${consultant.default_bill_rate}/hr`
            : '-'}
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap gap-1">
            {skillsList.slice(0, 3).map((skill, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {skill}
              </Badge>
            ))}
            {skillsList.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{skillsList.length - 3}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell>
          {consultant.is_active ? (
            <Badge className="bg-green-100 text-green-800">Active</Badge>
          ) : (
            <Badge variant="secondary">Inactive</Badge>
          )}
        </TableCell>
        <TableCell>
          <Switch
            checked={consultant.is_active}
            onCheckedChange={() => toggleConsultantStatus(consultant.id, consultant.is_active)}
            data-testid={`toggle-consultant-${consultant.id}`}
          />
        </TableCell>
        {user?.role === 'Admin' && (
          <TableCell>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openEditDialog(consultant)}
              data-testid={`edit-consultant-${consultant.id}`}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          </TableCell>
        )}
      </TableRow>
    );
  };

  const columnCount = user?.role === 'Admin' ? 10 : 9;

  if (loading) {
    return <LogoLoader message="Loading consultants..." />;
  }

  return (
    <div className="space-y-6" data-testid="consultants-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Consultants</h1>
          <p className="text-gray-600 mt-1">Manage your consultant team</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-utis-forest hover:bg-utis-forest/90 text-white" data-testid="add-consultant-button">
          <Plus className="w-4 h-4 mr-2" />
          Add Consultant
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Vendor</Label>
                <Select value={vendorFilter} onValueChange={setVendorFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Vendors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vendors</SelectItem>
                    {vendors.map((vendor) => (
                      <SelectItem key={vendor.id} value={String(vendor.id)}>
                        {vendor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="group-by-vendor"
                  checked={groupByVendor}
                  onCheckedChange={(checked) => setGroupByVendor(checked)}
                />
                <Label htmlFor="group-by-vendor" className="text-sm text-gray-700">
                  Group by Vendor
                </Label>
              </div>
            </div>
            {user?.role === 'Admin' && (
              <Button variant="outline" onClick={() => setShowVendorModal(true)} className="w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" /> New Vendor
              </Button>
            )}
          </div>
          {consultants.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No consultants yet. Click "Add Consultant" to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type / Payee</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Pay Rate</TableHead>
                  <TableHead>Bill Rate</TableHead>
                  <TableHead>Skills</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Active</TableHead>
                  {user?.role === 'Admin' && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupByVendor
                  ? groupedConsultants.map(([groupKey, groupConsultants]) => {
                      const headerLabel =
                        groupKey === 'individual'
                          ? 'Individuals (1099)'
                          : vendorMap.get(groupKey)?.name || `Vendor ${groupKey}`;
                      return (
                        <Fragment key={groupKey}>
                          <TableRow className="bg-gray-100/60">
                            <TableCell colSpan={columnCount} className="text-sm font-semibold text-gray-700">
                              {headerLabel}
                            </TableCell>
                          </TableRow>
                          {groupConsultants.map((consultant) => renderConsultantRow(consultant))}
                        </Fragment>
                      );
                    })
                  : filteredConsultants.map((consultant) => renderConsultantRow(consultant))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingConsultant ? 'Edit Consultant' : 'Add New Consultant'}</DialogTitle>
            <DialogDescription>
              {editingConsultant ? 'Update consultant information' : 'Add a new consultant to your team'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="John Doe" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required placeholder="john@example.com" />
              </div>
            </div>
            <div>
              <Label>Country</Label>
              <Input value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} placeholder="USA" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pay Rate ($/hr) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_pay_rate}
                  onChange={(e) => setFormData({ ...formData, default_pay_rate: e.target.value })}
                  required
                  placeholder="50.00"
                />
              </div>
              <div>
                <Label>Bill Rate ($/hr)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_bill_rate}
                  onChange={(e) => setFormData({ ...formData, default_bill_rate: e.target.value })}
                  placeholder="75.00"
                />
              </div>
            </div>
            <div>
              <Label>Consultant Type *</Label>
              <Select
                value={formData.consultant_type || 'Individual (1099)'}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    consultant_type: value,
                    vendor_id: value.includes('Vendor') ? prev.vendor_id : ''
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select consultant type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Individual (1099)">Individual (1099)</SelectItem>
                  <SelectItem value="Vendor-supplied (C2C)">Vendor-supplied (C2C)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.consultant_type?.includes('Vendor') && (
              <div className="space-y-2">
                <Label>Vendor *</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select
                    value={formData.vendor_id || ''}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, vendor_id: value }))}
                  >
                    <SelectTrigger className="sm:w-72">
                      <SelectValue placeholder="Select Vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((vendor) => (
                        <SelectItem key={vendor.id} value={String(vendor.id)}>
                          {vendor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {user?.role === 'Admin' && (
                    <Button type="button" variant="outline" onClick={() => setShowVendorModal(true)} className="sm:w-auto">
                      + New Vendor
                    </Button>
                  )}
                </div>
              </div>
            )}
            <div>
              <Label>HR Agent (External)</Label>
              <Select
                value={formData.hr_consultant_id || 'none'}
                onValueChange={(val) => setFormData({ ...formData, hr_consultant_id: val === 'none' ? '' : val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select HR agent (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {hrConsultants.map((hr) => (
                    <SelectItem key={hr.id} value={hr.id}>
                      {hr.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>HR Commission ($/hr)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.hr_commission_amount}
                onChange={(e) => setFormData({ ...formData, hr_commission_amount: e.target.value })}
                placeholder="2.00"
              />
              <p className="text-xs text-gray-500 mt-1">Fixed dollar amount HR agent earns per consultant hour</p>
            </div>

            {/* Mentoring / Supervision Section */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Mentoring & Supervision</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Supervising Consultant (Senior)</Label>
                  <Select
                    value={formData.supervising_consultant_id || 'none'}
                    onValueChange={(val) => setFormData({ ...formData, supervising_consultant_id: val === 'none' ? '' : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select supervisor (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {consultants
                        .filter((c) => c.is_active && c.mentoring_rate > 0)
                        .map((consultant) => (
                          <SelectItem key={consultant.id} value={consultant.id}>
                            {consultant.name} (${consultant.mentoring_rate}/hr)
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Senior who supervises this consultant</p>
                </div>
                <div>
                  <Label>Mentoring Rate ($/hr)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.mentoring_rate}
                    onChange={(e) => setFormData({ ...formData, mentoring_rate: e.target.value })}
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Earn this rate when mentoring juniors</p>
                </div>
              </div>
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
                <strong>Info:</strong> Senior consultants earn mentoring compensation = Junior's hours × Mentoring rate
              </div>
            </div>

            <div>
              <Label>Skills (comma-separated)</Label>
              <Input value={formData.skills} onChange={(e) => setFormData({ ...formData, skills: e.target.value })} placeholder="Python, React, AWS" />
            </div>
            <Button type="submit" className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white">
              {editingConsultant ? 'Update Consultant' : 'Create Consultant'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {user?.role === 'Admin' && (
        <Dialog open={showVendorModal} onOpenChange={handleVendorModalChange}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New Vendor</DialogTitle>
              <DialogDescription>Add a new vendor to your network.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleVendorSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={vendorForm.name} onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })} required />
                </div>
                <div>
                  <Label>Legal Name</Label>
                  <Input value={vendorForm.legal_name} onChange={(e) => setVendorForm({ ...vendorForm, legal_name: e.target.value })} />
                </div>
                <div>
                  <Label>Tax ID</Label>
                  <Input value={vendorForm.tax_id} onChange={(e) => setVendorForm({ ...vendorForm, tax_id: e.target.value })} />
                </div>
                <div>
                  <Label>Country</Label>
                  <Input value={vendorForm.country} onChange={(e) => setVendorForm({ ...vendorForm, country: e.target.value })} />
                </div>
                <div>
                  <Label>Currency</Label>
                  <Input value={vendorForm.currency} onChange={(e) => setVendorForm({ ...vendorForm, currency: e.target.value })} />
                </div>
                <div>
                  <Label>Payment Terms</Label>
                  <Input value={vendorForm.payment_terms} onChange={(e) => setVendorForm({ ...vendorForm, payment_terms: e.target.value })} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={vendorForm.email} onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={vendorForm.phone} onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input value={vendorForm.address} onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })} />
              </div>
              <div>
                <Label>Bank Details</Label>
                <Input value={vendorForm.bank_details} onChange={(e) => setVendorForm({ ...vendorForm, bank_details: e.target.value })} />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={vendorForm.notes} onChange={(e) => setVendorForm({ ...vendorForm, notes: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowVendorModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-utis-forest hover:bg-utis-forest/90 text-white">
                  Create Vendor
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Consultants;
